import {
  createAction,
  createSlice,
  PayloadAction,
  UnknownAction,
} from '@reduxjs/toolkit';
import {
  CurvePreset,
  MissionDraft,
  PlannerShape,
  ShapeKind,
} from '../../types/missionPlanner';
import { DistanceUnit } from '../../utils/distance';
import {
  CAPABILITY,
  ProfileAxis,
  clampValue,
  inRange,
} from '../../utils/profile';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Placement mode for the planner:
 *  - idle         — pointer pans/zooms the map normally
 *  - placing      — each map click appends a waypoint; Escape returns to idle
 *  - placing-home — the next map click sets the home base (single shot)
 */
export type PlannerPlacementMode = 'idle' | 'placing' | 'placing-home';

interface MissionPlannerState {
  /** The mission being authored. */
  draft: MissionDraft;
  placementMode: PlannerPlacementMode;
  /** Monotonic counter backing stable waypoint ids across reorders. */
  nextWaypointId: number;
  /** Display unit for all planner distances. */
  distanceUnit: DistanceUnit;
  /**
   * Whether to append a Return-to-Home leg (final waypoint → home base).
   * The home base defaults to the mission start (first waypoint) until a
   * custom one is set via `setHomeBase`. On by default so the mission closes
   * the loop back to the start unless the operator opts out (issue #342).
   */
  returnToHome: boolean;
  /** Active shape tool from the palette; a map drag draws this kind. */
  activeShapeTool: ShapeKind | null;
  /** Currently selected shape (for handles / bounding box / delete). */
  selectedShapeId: string | null;
  /** Monotonic counter backing stable shape ids. */
  nextShapeId: number;
  /**
   * Id of the waypoint currently hovered/focused in the list, or null. Shared
   * so the map can highlight that marker and its adjacent route segments.
   */
  hoveredWaypointId: string | null;
  /**
   * Whether the left-docked planner panel is collapsed to reclaim map space
   * (issue #343). View chrome — independent of the draft, so it survives
   * clears and loads.
   */
  panelCollapsed: boolean;
  /** Validation / interaction error surfaced to the operator. */
  error: string | null;
  /**
   * Server-persistence state (#321). Identifies the saved draft currently open
   * so Save updates it (vs Save as creating a new one) and so a remote change
   * can be matched against what the operator has loaded. Null = unsaved draft.
   */
  /**
   * Drone this draft is planned for, or null when none is chosen (#451). Not
   * part of the mission document and deliberately outside the undo snapshot,
   * like the draft's saved name: undoing a waypoint drag must not silently
   * reassign the drone.
   */
  droneId: string | null;
  savedDraftId: string | null;
  savedDraftName: string | null;
  /** Base revision for optimistic-concurrency on the next update. */
  savedRevision: number | null;
  /**
   * Set when another client saved a newer revision of the open draft (via the
   * mission_draft_updated WS event) — surfaces a "newer version available"
   * banner. Auto-merge is out of scope (V2).
   */
  staleRevisionAvailable: boolean;
  /**
   * What was saved, as plannerFingerprint has it, so the Planner can tell a
   * saved draft from one edited since (#632, F4): only the saved route is
   * flown, so a launch is offered only while the two are the same. Null when
   * nothing has been saved or opened.
   */
  savedFingerprint: string | null;
}

/**
 * The part of the planner state a saved draft holds: the route, its
 * returnToHome and its drone. Two equal fingerprints are the same saved route.
 */
export const plannerFingerprint = (s: {
  draft: MissionDraft;
  returnToHome: boolean;
  droneId: string | null;
}): string => JSON.stringify([s.draft, s.returnToHome, s.droneId]);

const DEFAULT_PROFILE = {
  cruiseSpeedMs: 10,
  altitudeM: 50,
  altitudeCurve: { points: [], preset: 'linear' as CurvePreset },
  speedCurve: { points: [], preset: 'linear' as CurvePreset },
};

/** The curve for an axis within a draft profile. */
const curveFor = (draft: MissionDraft, axis: ProfileAxis) =>
  axis === 'altitude' ? draft.profile.altitudeCurve : draft.profile.speedCurve;

/** Loud, human message when a value is clamped to the capability envelope. */
const rangeError = (axis: ProfileAxis): string => {
  const c = CAPABILITY[axis];
  return `${c.label} out of range — clamped to ${c.min}–${c.max} ${c.unit}.`;
};

const emptyDraft = (): MissionDraft => ({
  waypoints: [],
  homeBase: null,
  shapes: [],
  profile: {
    ...DEFAULT_PROFILE,
    // Fresh curve objects so drafts never share point arrays.
    altitudeCurve: { points: [], preset: 'linear' },
    speedCurve: { points: [], preset: 'linear' },
  },
});

const initialState: MissionPlannerState = {
  draft: emptyDraft(),
  placementMode: 'idle',
  nextWaypointId: 1,
  distanceUnit: 'km',
  // Close the loop back to the start by default (issue #342).
  returnToHome: true,
  activeShapeTool: null,
  selectedShapeId: null,
  nextShapeId: 1,
  hoveredWaypointId: null,
  panelCollapsed: false,
  error: null,
  droneId: null,
  savedDraftId: null,
  savedDraftName: null,
  savedRevision: null,
  staleRevisionAvailable: false,
  savedFingerprint: null,
};

/**
 * Next monotonic counter for ids of the form `<prefix><n>` in a set of items.
 * Used after hydrating a saved draft so freshly added items never collide with
 * loaded ones.
 */
const nextCounterFrom = (ids: string[], prefix: string): number => {
  let max = 0;
  for (const id of ids) {
    const n = Number.parseInt(id.slice(prefix.length), 10);
    if (id.startsWith(prefix) && Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
};

/** Re-number waypoints 1..N in array order after an add/remove. */
const renumber = (waypoints: MissionDraft['waypoints']): void => {
  waypoints.forEach((wp, i) => {
    wp.order = i + 1;
  });
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const missionPlannerSlice = createSlice({
  name: 'missionPlanner',
  initialState,
  reducers: {
    /** Operator clicked "Add waypoints" — start capturing map clicks. */
    startPlacement(state) {
      state.placementMode = 'placing';
      state.activeShapeTool = null;
      state.error = null;
    },
    /** Operator pressed Escape or clicked Done — stop capturing clicks. */
    cancelPlacement(state) {
      state.placementMode = 'idle';
    },
    /** Operator chose "set home base on map" — next click sets it. */
    startHomeBasePlacement(state) {
      state.placementMode = 'placing-home';
      state.activeShapeTool = null;
      state.error = null;
    },
    /**
     * Set a custom home base (map click or GPS input) and leave placement.
     * A set home base overrides the default (mission start point).
     */
    setHomeBase(state, action: PayloadAction<{ lat: number; lng: number }>) {
      state.draft.homeBase = {
        lat: action.payload.lat,
        lng: action.payload.lng,
      };
      state.placementMode = 'idle';
    },
    /** Clear the custom home base — RTH falls back to the start point. */
    clearHomeBase(state) {
      state.draft.homeBase = null;
    },
    /** Toggle the Return-to-Home leg on/off. */
    setReturnToHome(state, action: PayloadAction<boolean>) {
      state.returnToHome = action.payload;
    },
    /** Select a shape tool from the palette (null clears it). Exits waypoint/home placement. */
    setActiveShapeTool(state, action: PayloadAction<ShapeKind | null>) {
      state.activeShapeTool = action.payload;
      if (action.payload) {
        state.placementMode = 'idle';
      }
    },
    /**
     * Add a freshly drawn shape. Selects it and returns to select mode so it
     * can be immediately resized/rotated; draw another by re-picking the tool.
     */
    addShape(state, action: PayloadAction<Omit<PlannerShape, 'id'>>) {
      const id = `shape-${state.nextShapeId}`;
      state.nextShapeId += 1;
      state.draft.shapes.push({ id, ...action.payload });
      state.selectedShapeId = id;
      state.activeShapeTool = null;
    },
    /** Update a shape's geometry (resize / rotate / move). Kind is immutable. */
    updateShape(
      state,
      action: PayloadAction<{
        id: string;
        changes: Partial<Omit<PlannerShape, 'id' | 'kind'>>;
      }>
    ) {
      const shape = state.draft.shapes.find((s) => s.id === action.payload.id);
      if (shape) {
        Object.assign(shape, action.payload.changes);
      }
    },
    /** Delete a shape (Delete key / context menu / list). */
    removeShape(state, action: PayloadAction<string>) {
      state.draft.shapes = state.draft.shapes.filter(
        (s) => s.id !== action.payload
      );
      if (state.selectedShapeId === action.payload) {
        state.selectedShapeId = null;
      }
    },
    /** Select a shape (null clears selection). */
    selectShape(state, action: PayloadAction<string | null>) {
      state.selectedShapeId = action.payload;
    },
    /** Map click while placing — append a waypoint at the clicked location. */
    addWaypoint(state, action: PayloadAction<{ lat: number; lng: number }>) {
      const id = `wp-${state.nextWaypointId}`;
      state.nextWaypointId += 1;
      state.draft.waypoints.push({
        id,
        lat: action.payload.lat,
        lng: action.payload.lng,
        order: state.draft.waypoints.length + 1,
      });
    },
    /**
     * Insert a waypoint into an existing leg, after `index` (0-based position
     * of the leg's start point), and renumber. Backs dragging a leg's midpoint
     * handle to split it in two (#433).
     */
    insertWaypointAfter(
      state,
      action: PayloadAction<{ index: number; lat: number; lng: number }>
    ) {
      const { index, lat, lng } = action.payload;
      if (index < 0 || index >= state.draft.waypoints.length) {
        return;
      }
      const id = `wp-${state.nextWaypointId}`;
      state.nextWaypointId += 1;
      state.draft.waypoints.splice(index + 1, 0, { id, lat, lng, order: 0 });
      renumber(state.draft.waypoints);
    },
    /**
     * Append several waypoints in one go — used by survey-path generation
     * (#347). A single action so the whole generated path is one undo entry,
     * and the points land as ordinary draft waypoints the operator can review,
     * drag and delete before exporting; nothing is auto-committed.
     */
    addWaypoints(
      state,
      action: PayloadAction<Array<{ lat: number; lng: number }>>
    ) {
      for (const { lat, lng } of action.payload) {
        state.draft.waypoints.push({
          id: `wp-${state.nextWaypointId}`,
          lat,
          lng,
          order: state.draft.waypoints.length + 1,
        });
        state.nextWaypointId += 1;
      }
    },
    /** Drag ended — update the waypoint's coordinates in place. */
    moveWaypoint(
      state,
      action: PayloadAction<{ id: string; lat: number; lng: number }>
    ) {
      const wp = state.draft.waypoints.find((w) => w.id === action.payload.id);
      if (wp) {
        wp.lat = action.payload.lat;
        wp.lng = action.payload.lng;
      }
    },
    /** Remove a waypoint and re-number the remainder. */
    removeWaypoint(state, action: PayloadAction<string>) {
      state.draft.waypoints = state.draft.waypoints.filter(
        (w) => w.id !== action.payload
      );
      renumber(state.draft.waypoints);
    },
    /**
     * Move the waypoint at index `from` to index `to`, then renumber. Backs
     * drag-and-drop, the Move Up/Down context menu, and Alt+Up/Down. Out-of-
     * range or no-op indices are ignored.
     */
    reorderWaypoints(
      state,
      action: PayloadAction<{ from: number; to: number }>
    ) {
      const { from, to } = action.payload;
      const wps = state.draft.waypoints;
      if (
        from < 0 ||
        from >= wps.length ||
        to < 0 ||
        to >= wps.length ||
        from === to
      ) {
        return;
      }
      const [moved] = wps.splice(from, 1);
      wps.splice(to, 0, moved);
      renumber(wps);
    },
    /** List hover/focus → highlight the matching marker + adjacent segments. */
    setHoveredWaypoint(state, action: PayloadAction<string | null>) {
      state.hoveredWaypointId = action.payload;
    },
    /** Collapse / expand the left planner panel to reclaim map space (#343). */
    setPanelCollapsed(state, action: PayloadAction<boolean>) {
      state.panelCollapsed = action.payload;
    },
    /** Change the display unit for distances (persists across draft clears). */
    setDistanceUnit(state, action: PayloadAction<DistanceUnit>) {
      state.distanceUnit = action.payload;
    },
    /** Update the flight profile (cruise speed / altitude). */
    setProfile(
      state,
      action: PayloadAction<Partial<MissionDraft['profile']>>
    ) {
      state.draft.profile = { ...state.draft.profile, ...action.payload };
    },
    /**
     * Set a global default (altitude / speed), clamped to the drone capability
     * envelope. Out-of-range input is clamped and surfaced loudly (fail-safe).
     */
    setProfileDefault(
      state,
      action: PayloadAction<{ axis: ProfileAxis; value: number }>
    ) {
      const { axis, value } = action.payload;
      const clamped = clampValue(axis, value);
      if (axis === 'altitude') state.draft.profile.altitudeM = clamped;
      else state.draft.profile.cruiseSpeedMs = clamped;
      state.error = inRange(axis, value)
        ? null
        : rangeError(axis);
    },
    /** Set the interpolation preset for a curve. */
    setCurvePreset(
      state,
      action: PayloadAction<{ axis: ProfileAxis; preset: CurvePreset }>
    ) {
      curveFor(state.draft, action.payload.axis).preset = action.payload.preset;
    },
    /** Add a control point (click on the curve). Value clamped + flagged loud. */
    addControlPoint(
      state,
      action: PayloadAction<{ axis: ProfileAxis; distanceKm: number; value: number }>
    ) {
      const { axis, distanceKm, value } = action.payload;
      const clamped = clampValue(axis, value);
      const curve = curveFor(state.draft, axis);
      curve.points.push({ distanceKm: Math.max(0, distanceKm), value: clamped });
      curve.points.sort((a, b) => a.distanceKm - b.distanceKm);
      state.error = inRange(axis, value) ? null : rangeError(axis);
    },
    /** Move a control point (drag). Value clamped + flagged loud. */
    moveControlPoint(
      state,
      action: PayloadAction<{
        axis: ProfileAxis;
        index: number;
        distanceKm: number;
        value: number;
      }>
    ) {
      const { axis, index, distanceKm, value } = action.payload;
      const curve = curveFor(state.draft, axis);
      const point = curve.points[index];
      if (!point) return;
      point.distanceKm = Math.max(0, distanceKm);
      point.value = clampValue(axis, value);
      curve.points.sort((a, b) => a.distanceKm - b.distanceKm);
      state.error = inRange(axis, value) ? null : rangeError(axis);
    },
    /** Remove a control point (double-click). */
    removeControlPoint(
      state,
      action: PayloadAction<{ axis: ProfileAxis; index: number }>
    ) {
      const curve = curveFor(state.draft, action.payload.axis);
      curve.points.splice(action.payload.index, 1);
    },
    /** Surface a validation / interaction error to the operator. */
    setPlannerError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    /**
     * Replace the current draft's route with an imported/loaded one (issue
     * #299). Waypoints get fresh sequential ids and 1..N ordering; the home base
     * is applied when present. Profile and shapes reset to defaults, since the
     * standard interchange formats don't carry them. Returns to idle.
     */
    loadDraft(
      state,
      action: PayloadAction<{
        waypoints: Array<{ lat: number; lng: number }>;
        homeBase?: { lat: number; lng: number } | null;
      }>
    ) {
      const fresh = emptyDraft();
      action.payload.waypoints.forEach((wp, i) => {
        fresh.waypoints.push({
          id: `wp-${i + 1}`,
          lat: wp.lat,
          lng: wp.lng,
          order: i + 1,
        });
      });
      fresh.homeBase = action.payload.homeBase ?? null;
      state.draft = fresh;
      state.nextWaypointId = action.payload.waypoints.length + 1;
      state.placementMode = 'idle';
      state.activeShapeTool = null;
      state.selectedShapeId = null;
      state.nextShapeId = 1;
      state.hoveredWaypointId = null;
      state.error = null;
    },
    /**
     * Replace the draft with a full saved draft loaded from the backend (#321).
     * Unlike loadDraft (interchange import), this restores everything —
     * waypoints, shapes, profile, home base and the returnToHome toggle — and
     * records the draft's server identity/revision so Save updates it in place.
     * Waypoint/shape id counters are advanced past the loaded ids.
     */
    hydrateDraft(
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        revision: number;
        draft: MissionDraft;
        returnToHome: boolean;
        /** Drone the saved draft was planned for, if any (#451). */
        droneId?: string | null;
      }>
    ) {
      const { id, name, revision, draft, returnToHome, droneId } = action.payload;
      state.draft = draft;
      state.returnToHome = returnToHome;
      state.droneId = droneId ?? null;
      state.nextWaypointId = nextCounterFrom(
        draft.waypoints.map((w) => w.id),
        'wp-'
      );
      state.nextShapeId = nextCounterFrom(
        draft.shapes.map((s) => s.id),
        'shape-'
      );
      state.savedDraftId = id;
      state.savedDraftName = name;
      state.savedRevision = revision;
      state.staleRevisionAvailable = false;
      state.savedFingerprint = plannerFingerprint(state);
      state.placementMode = 'idle';
      state.activeShapeTool = null;
      state.selectedShapeId = null;
      state.hoveredWaypointId = null;
      state.error = null;
    },
    /**
     * Record a successful save/update: the open draft now maps to this server
     * id/revision, so the next Save updates in place at the correct base
     * revision. Clears any stale-revision banner.
     */
    draftSaved(
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        revision: number;
        /**
         * plannerFingerprint of what was sent, taken before the request: an
         * edit made while it was in flight is not what was saved.
         */
        fingerprint: string;
      }>
    ) {
      state.savedDraftId = action.payload.id;
      state.savedDraftName = action.payload.name;
      state.savedRevision = action.payload.revision;
      state.staleRevisionAvailable = false;
      state.savedFingerprint = action.payload.fingerprint;
    },
    /**
     * A mission_draft_updated WS event arrived. If it concerns the open draft
     * and carries a newer revision than the one loaded (i.e. another client
     * saved), raise the stale-revision banner. Our own save echoes at the same
     * revision, so it never self-triggers.
     */
    missionDraftRemotelyUpdated(
      state,
      action: PayloadAction<{ id: string; revision: number; deleted?: boolean }>
    ) {
      if (action.payload.id !== state.savedDraftId) return;
      if (action.payload.deleted) {
        state.staleRevisionAvailable = true;
        return;
      }
      if (
        state.savedRevision !== null &&
        action.payload.revision > state.savedRevision
      ) {
        state.staleRevisionAvailable = true;
      }
    },
    /** Dismiss the "newer version available" banner. */
    dismissStaleBanner(state) {
      state.staleRevisionAvailable = false;
    },
    /** Discard the whole draft and return to idle. */
    clearDraft(state) {
      state.draft = emptyDraft();
      state.placementMode = 'idle';
      state.nextWaypointId = 1;
      // Reset to the close-the-loop default (issue #342).
      state.returnToHome = true;
      state.activeShapeTool = null;
      state.selectedShapeId = null;
      state.nextShapeId = 1;
      state.hoveredWaypointId = null;
      state.error = null;
      state.droneId = null;
      state.savedDraftId = null;
      state.savedDraftName = null;
      state.savedRevision = null;
      state.savedFingerprint = null;
      state.staleRevisionAvailable = false;
    },
    /**
     * Assign (or clear) the drone this draft is planned for (#451). A geofence
     * is assigned per drone, so choosing one is what opts the draft into being
     * checked — against the map while editing, and by the server on save.
     */
    setDraftDrone(state, action: PayloadAction<string | null>) {
      state.droneId = action.payload;
    },
  },
});

export const {
  startPlacement,
  cancelPlacement,
  startHomeBasePlacement,
  setHomeBase,
  clearHomeBase,
  setReturnToHome,
  setActiveShapeTool,
  addShape,
  updateShape,
  removeShape,
  selectShape,
  setProfileDefault,
  setCurvePreset,
  addControlPoint,
  moveControlPoint,
  removeControlPoint,
  addWaypoint,
  addWaypoints,
  insertWaypointAfter,
  moveWaypoint,
  removeWaypoint,
  reorderWaypoints,
  setHoveredWaypoint,
  setPanelCollapsed,
  setDistanceUnit,
  setProfile,
  setPlannerError,
  setDraftDrone,
  loadDraft,
  clearDraft,
  hydrateDraft,
  draftSaved,
  missionDraftRemotelyUpdated,
  dismissStaleBanner,
} = missionPlannerSlice.actions;

// ---------------------------------------------------------------------------
// Undo / redo history (issue #300)
//
// A thin higher-order reducer wraps the slice so the last N document-editing
// operations can be undone and redone. Only the *mission document* is tracked
// (draft + id/mode counters that keep ids unique across undo); transient UI
// state (placement mode, selection, hover, errors, distance unit) is not, so
// undo restores the route/shapes/profile without resurrecting stale UI.
// ---------------------------------------------------------------------------

/** Cap on how many operations can be undone (acceptance criterion: 10). */
export const HISTORY_LIMIT = 10;

/** Undo the last document-editing operation. */
export const undo = createAction('missionPlanner/undo');
/** Redo the last undone operation. */
export const redo = createAction('missionPlanner/redo');

/** The slice of state that defines the mission document (snapshotted for undo). */
interface Snapshot {
  draft: MissionDraft;
  nextWaypointId: number;
  nextShapeId: number;
  returnToHome: boolean;
}

/** Planner state augmented with the undo/redo history stacks. */
export interface HistoriedPlannerState extends MissionPlannerState {
  past: Snapshot[];
  future: Snapshot[];
  /**
   * Key of the last coalescing action (a continuous drag), or null. Lets a
   * whole drag — which dispatches many move actions — collapse into a single
   * undo entry instead of one per pixel.
   */
  coalesceKey: string | null;
}

const takeSnapshot = (s: MissionPlannerState): Snapshot => ({
  draft: s.draft,
  nextWaypointId: s.nextWaypointId,
  nextShapeId: s.nextShapeId,
  returnToHome: s.returnToHome,
});

const snapshotChanged = (a: MissionPlannerState, b: MissionPlannerState): boolean =>
  a.draft !== b.draft ||
  a.nextWaypointId !== b.nextWaypointId ||
  a.nextShapeId !== b.nextShapeId ||
  a.returnToHome !== b.returnToHome;

/** Action types that create an undo checkpoint (they edit the document). */
const UNDOABLE = new Set<string>([
  addWaypoint.type,
  addWaypoints.type,
  insertWaypointAfter.type,
  moveWaypoint.type,
  removeWaypoint.type,
  reorderWaypoints.type,
  setHomeBase.type,
  clearHomeBase.type,
  setReturnToHome.type,
  addShape.type,
  updateShape.type,
  removeShape.type,
  setProfile.type,
  setProfileDefault.type,
  setCurvePreset.type,
  addControlPoint.type,
  moveControlPoint.type,
  removeControlPoint.type,
  loadDraft.type,
  hydrateDraft.type,
  clearDraft.type,
]);

/**
 * Coalescing key for a continuous drag, or null for a discrete edit. Two
 * consecutive actions with the same key share one undo entry (the pre-drag
 * snapshot), so undoing a drag reverts the whole gesture at once.
 */
const coalesceKeyFor = (action: UnknownAction): string | null => {
  if (action.type === moveWaypoint.type) {
    const p = (action as ReturnType<typeof moveWaypoint>).payload;
    return `mw:${p.id}`;
  }
  if (action.type === moveControlPoint.type) {
    const p = (action as ReturnType<typeof moveControlPoint>).payload;
    return `mc:${p.axis}:${p.index}`;
  }
  // Shape gestures stream updates: the body drag (#346) and the resize/rotate
  // handles (#435). Keying on the fields touched keeps a whole gesture in one
  // undo entry while a differently-shaped edit still starts a new one.
  if (action.type === updateShape.type) {
    const p = (action as ReturnType<typeof updateShape>).payload;
    const fields = Object.keys(p.changes).sort().join(',');
    return fields ? `ms:${p.id}:${fields}` : null;
  }
  return null;
};

const baseReducer = missionPlannerSlice.reducer;

/** Slice reducer wrapped with bounded undo/redo history. */
const historiedReducer = (
  state: HistoriedPlannerState | undefined,
  action: UnknownAction
): HistoriedPlannerState => {
  if (state === undefined) {
    return {
      ...baseReducer(undefined, action),
      past: [],
      future: [],
      coalesceKey: null,
    };
  }

  if (action.type === undo.type) {
    if (state.past.length === 0) return state;
    const previous = state.past[state.past.length - 1];
    return {
      ...state,
      ...previous,
      past: state.past.slice(0, -1),
      future: [takeSnapshot(state), ...state.future].slice(0, HISTORY_LIMIT),
      coalesceKey: null,
    };
  }

  if (action.type === redo.type) {
    if (state.future.length === 0) return state;
    const next = state.future[0];
    return {
      ...state,
      ...next,
      past: [...state.past, takeSnapshot(state)].slice(-HISTORY_LIMIT),
      future: state.future.slice(1),
      coalesceKey: null,
    };
  }

  const nextInner = baseReducer(state, action);
  const key = coalesceKeyFor(action);

  if (UNDOABLE.has(action.type) && snapshotChanged(state, nextInner)) {
    // Same drag as the previous action → extend it rather than add an entry.
    const coalesce = key !== null && key === state.coalesceKey;
    return {
      ...nextInner,
      past: coalesce
        ? state.past
        : [...state.past, takeSnapshot(state)].slice(-HISTORY_LIMIT),
      future: [],
      coalesceKey: key,
    };
  }

  return {
    ...nextInner,
    past: state.past,
    future: state.future,
    coalesceKey: key,
  };
};

export default historiedReducer;
